<template>
  <div class="bg-white">
    <div class="d-flex align-items-center gap-2 bg-light manage-header">
      <BackButton class="ml-n3 mr-n2" :class="backClass" />
      <h4 class="m-0 text-truncate" :title="title">{{title}}</h4>
      <slot name="header-buttons" :items="items">
        <div class="ml-auto"></div>
        <ManageSearch/>
        <ColumnPickerManage
            :columns="columns"
            :fields="fields"
            :fields-active.sync="fields_"
            :item="items?.[0]"
            @input="query = {...query, columns: $event}"
        />
        <slot name="header-buttons-extra"></slot>
        <DownloadButton :items="items"/>
        <NewButton :label="newTitle"/>
      </slot>
    </div>

    <ErrorDisplay :value="error" class="m-3"/>

    <ManageListTable
      :items="items"
      :fields="decorateFields(fields_)"
      :loading="loading"
      :get-to="getTo"
      :tbody-tr-class="tbodyTrClass"
      :sort-by="ordering?.split(',')[0]?.replace(/^-/, '')"
      :sort-desc="ordering?.split(',')[0]?.indexOf('-') === 0"
      @sort-changed="onSortChanged"
      no-sort-reset
      no-local-sorting
    />

    <Pagination
      v-if="total"
      :total="total"
      :limit="limit_"
      :limit-options="limitOptions"
      class="container-fluid mb-3"
    />
  </div>
</template>

<script>
import { mapValues } from 'lodash'

import BackButton from '@/components/BackButton.vue'
import Pagination from '@/components/Pagination.vue'
import ColumnPickerManage from './-column-picker-manage.vue'
import ManageSearch from '@/pages/manage/-search.vue'
import DownloadButton from './-download-button.vue'
import NewButton from './-new-button.vue'
import ManageListTable from './-table.vue'
import { typeTitle } from '@/pages/_type/index.vue'
import { http } from '@/services/http.js'
import { MANAGE_LIMIT_DEFAULT } from '@/services/constants.js'
import { typePlural } from '@/pages/_type/index.vue'
import { datetimeRelative } from '@/utils/datetimeRelative.js'

export default {
  name: 'ManageList',
  components: {
    BackButton,
    ColumnPickerManage,
    DownloadButton,
    ManageSearch,
    NewButton,
    Pagination,
    ManageListTable,
  },
  props: {
    type: String,
    fields: {
      type: Array,
      default: () => ([
        {key: 'name', sortable: true},
        {key: 'created', sortable: true, autoshow: 'sm'},
      ]),
    },
    columns: String,
    limit: {
      default: MANAGE_LIMIT_DEFAULT,
    },
    limitOptions: {
      default: () => [10, 100, 1000],
    },
    ordering: {
      type: String,
      default: '-created',
    },
    backClass: {
      default: 'd-sm-none'
    },
    tbodyTrClass: null,
  },
  data() {
    return {
      loading: false,
      error: null,
      items: [],
      total: null,
      limit_: this.limit,
      fields_: this.fields,
    }
  },
  computed: {
    query: {
      get() {
        return this.$route.query
      },
      set(val) {
        return this.$router.replace({query: val})
      },
    },
    title() {
      return typePlural(this.type)
    },
    newTitle() {
      return `New ${typeTitle(this.type)}`
    },
  },
  watch: {
    limit(val) {
      this.limit_ = val
    },
  },
  async created() {
    this.$nextTick(() => {
      document.title = `${this.title} - Great American Art`
    })
    this.fetchItems()
      .catch(err => {
        this.error = err
        throw err
      })
  },
  methods: {
    decorateFields,
    onSortChanged({sortBy, sortDesc}) {
      const ordering = (sortDesc ? '-' : '') + sortBy
      this.query = smartReplace({...this.query, offset: undefined, ordering}, this)
    },
    async fetchItems() {
      this.loading = true
      const data = await http.get(`api/${this.type}/`, {params: this.$route.query})
        .then(response => response.data)
        .finally(() => this.loading = false)
      this.items = data.results
      this.total = data.count
    },
    getTo(item) {
      return { path: String(item.id), append: true }
    },
  },
}

export function smartReplace(query, context) {
  // Set query params to undefined when desired value matches default.
  query = mapValues(query, (val, key) => {
    const propDefault = context.$options.props?.[key]?.default
    const defaultVal = propDefault?.call?.(context) ?? propDefault
    if (val === defaultVal) {
      return undefined
    }
    return val
  })
  return query
}

export function decorateFields(fields) {
  return fields?.map(field => {
    let defaults = { sortable: true, thClass: 'text-nowrap' }
    const isDate = ['created', 'modified', 'createdOn', 'expiresOn'].includes(field.key)
      || field.key.startsWith('date_')
      || field.key.endsWith('_date')
    if (isDate) {
      defaults = {
        ...defaults,
        formatter: i => i && datetimeRelative(i).replace(',', ' '),
        tdAttr: i => i && ({title: i}),
        tdClass: 'whitespace-pre-wrap',
      }
    }
    return { ...defaults, ...field }
  })
}

</script>

